import { createTheme } from '@mui/material/styles';
import { COLORS } from '../utils/constants';

// Light theme - "Large Print" Edition
export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: COLORS.PRIMARY,
      light: COLORS.PRIMARY_LIGHT,
      dark: COLORS.PRIMARY_DARK,
      contrastText: '#ffffff',
    },
    secondary: {
      main: COLORS.SECONDARY,
      light: COLORS.SECONDARY_LIGHT,
      dark: COLORS.SECONDARY_DARK,
      contrastText: '#ffffff',
    },
    text: {
      primary: '#2C1810', // Deep Coffee - High Contrast
      secondary: '#4A3B32', // Darker secondary for readability
    },
    error: {
      main: COLORS.ERROR,
    },
    warning: {
      main: COLORS.WARNING,
    },
    info: {
      main: COLORS.INFO,
    },
    success: {
      main: COLORS.SUCCESS,
    },
    background: {
      default: COLORS.WARM_WHITE,
      paper: '#ffffff',
    },
    grey: {
      50: COLORS.WARM_WHITE,
      100: COLORS.CREAM,
      200: '#EDE7DF',
      300: '#D6C7B8',
      400: '#B8A394',
      500: COLORS.WARM_GRAY,
      600: '#7A6B5A',
      700: '#655649',
      800: '#4D4037',
      900: '#362B23',
    },
  },
  typography: {
    fontFamily: [
      'Merriweather', // Primary book font
      'Inter',
      'Roboto',
      '"Segoe UI"',
      '"Helvetica Neue"',
      'Arial',
      'serif',
    ].join(','),
    h1: {
      fontSize: '3rem', // Increased
      fontWeight: 700,
      letterSpacing: '-0.02em',
      lineHeight: 1.2,
      fontFamily: '"Playfair Display", serif',
    },
    h2: {
      fontSize: '2.5rem', // Increased
      fontWeight: 600,
      letterSpacing: '-0.01em',
      lineHeight: 1.3,
      fontFamily: '"Playfair Display", serif',
    },
    h3: {
      fontSize: '2rem', // Increased
      fontWeight: 600,
      letterSpacing: '-0.01em',
      lineHeight: 1.3,
      fontFamily: '"Playfair Display", serif',
    },
    h4: {
      fontSize: '1.75rem', // Increased
      fontWeight: 600,
      letterSpacing: '0em',
      lineHeight: 1.4,
      fontFamily: '"Playfair Display", serif',
    },
    h5: {
      fontSize: '1.5rem', // Increased
      fontWeight: 600,
      letterSpacing: '0em',
      lineHeight: 1.4,
      fontFamily: '"Playfair Display", serif',
    },
    h6: {
      fontSize: '1.25rem', // Increased
      fontWeight: 600,
      letterSpacing: '0em',
      lineHeight: 1.4,
      fontFamily: '"Playfair Display", serif',
    },
    body1: {
      fontSize: '1.125rem', // 18px - Large Print standard
      fontWeight: 400,
      lineHeight: 1.7,
      letterSpacing: '0.01em',
      fontFamily: '"Merriweather", serif',
    },
    body2: {
      fontSize: '1rem', // 16px - Minimum legible size
      fontWeight: 400,
      lineHeight: 1.6,
      letterSpacing: '0.01em',
      fontFamily: '"Merriweather", serif',
    },
    caption: {
      fontSize: '0.875rem', // 14px - Increased from 12px
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: '0.02em',
      fontFamily: '"Merriweather", serif',
    },
    button: {
      fontSize: '1rem',
      fontWeight: 600,
      fontFamily: '"Inter", sans-serif', // Buttons can stay clear sans-serif
    }
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 12,
          padding: '16px 28px', // Larger touch target
          fontWeight: 600,
          fontSize: '1.1rem', // Larger text
          letterSpacing: '0.01em',
        },
        contained: {
          boxShadow: '0px 4px 12px rgba(255, 107, 53, 0.25)',
          '&:hover': {
            boxShadow: '0px 6px 20px rgba(255, 107, 53, 0.35)',
            transform: 'translateY(-1px)',
          },
          '&:active': {
            transform: 'translateY(0px)',
          },
        },
        outlined: {
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
          },
        },
        sizeLarge: {
          padding: '20px 36px',
          fontSize: '1.2rem',
        },
        sizeSmall: {
          padding: '10px 20px', // Even small buttons are big enough to tap
          fontSize: '0.95rem',
        }
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(0, 0, 0, 0.04)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.12)',
            transform: 'translateY(-2px)',
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '24px', // More breathing room
          '&:last-child': {
            paddingBottom: '24px',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            backgroundColor: '#ffffff',
            '& fieldset': {
              borderColor: 'rgba(44, 24, 16, 0.2)', // Darker border
            },
            '&:hover fieldset': {
              borderColor: 'rgba(44, 24, 16, 0.4)',
            },
            '&.Mui-focused fieldset': {
              borderWidth: 2,
              borderColor: COLORS.PRIMARY,
            },
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0px 2px 16px rgba(0, 0, 0, 0.08)',
          backgroundColor: '#ffffff',
          color: '#2C1810',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: 12, // Larger touch target
          '&:hover': {
            backgroundColor: 'rgba(255, 107, 53, 0.08)',
          },
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          boxShadow: '0px 6px 20px rgba(255, 107, 53, 0.3)',
          '&:hover': {
            boxShadow: '0px 8px 28px rgba(255, 107, 53, 0.4)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          height: 36, // Taller chips
          fontSize: '0.95rem',
        },
        label: {
          paddingLeft: 12,
          paddingRight: 12,
        },
      },
    },
  },
  shape: {
    borderRadius: 12,
  },
  spacing: 8,
});
